import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type { FindIngestionSettingsQueryResponse } from "../types/FindIngestionSettings"

type FindIngestionSettingsClient = typeof client<
  FindIngestionSettingsQueryResponse,
  Error,
  never
>
type FindIngestionSettings = {
  data: FindIngestionSettingsQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindIngestionSettingsClient>>
  client: {
    parameters: Partial<Parameters<FindIngestionSettingsClient>[0]>
    return: Awaited<ReturnType<FindIngestionSettingsClient>>
  }
}
export const findIngestionSettingsQueryKey = () =>
  [{ url: "/v1/client-settings/find-ingestion-settings" }] as const
export type FindIngestionSettingsQueryKey = ReturnType<
  typeof findIngestionSettingsQueryKey
>
export function findIngestionSettingsQueryOptions<
  TData = FindIngestionSettings["response"],
  TQueryData = FindIngestionSettings["response"],
>(
  options: FindIngestionSettings["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindIngestionSettings["response"],
    FindIngestionSettings["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findIngestionSettingsQueryKey()
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindIngestionSettings["data"],
        FindIngestionSettings["error"]
      >({
        method: "get",
        url: `/v1/client-settings/find-ingestion-settings`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find ingestion settings
 * @link /v1/client-settings/find-ingestion-settings
 */
export function useFindIngestionSettings<
  TData = FindIngestionSettings["response"],
  TQueryData = FindIngestionSettings["response"],
  TQueryKey extends QueryKey = FindIngestionSettingsQueryKey,
>(
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindIngestionSettings["response"],
        FindIngestionSettings["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindIngestionSettings["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindIngestionSettings["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findIngestionSettingsQueryKey()
  const query = useQuery<
    FindIngestionSettings["data"],
    FindIngestionSettings["error"],
    TData,
    any
  >({
    ...findIngestionSettingsQueryOptions<TData, TQueryData>(clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindIngestionSettings["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
